<script setup lang="ts">
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import MoodFace from '@/vue/atoms/mood-face.vue';
import {useMoods} from '@/ts/composables/pure/use-moods';
import {useJournalStamp} from '@/ts/composables/stateful/use-journal-stamp';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';

const props = defineProps<{
  mood: MoodDTO;
  text?: string | null;
  /** ISO-8601; formatted here, ordered by the profile's country and worded by its language. */
  date?: string;
}>();

const {t} = useI18n();
const {getMood, getMoodTint} = useMoods();
const {formatStamp} = useJournalStamp();

const stamp = computed(() => formatStamp(props.date));

// Uppercased in the template via CSS would break languages with different
// casing rules, so uppercase the translated label directly.
const label = computed(() => t(`mood_${props.mood}`).toUpperCase());
const tint = computed(() => getMoodTint(props.mood));
/** No note → the face becomes the card's content instead of a corner badge. */
const moodOnly = computed(() => !props.text);
const known = computed(() => Boolean(getMood(props.mood)));
</script>

<template>
  <div v-if="known" class="jcard" :style="{background: tint}">
    <div class="top">
      <span class="pill">{{ label }}</span>
      <mood-face v-if="!moodOnly" :mood="mood" :size="38"/>
    </div>

    <div v-if="moodOnly" class="big-face">
      <mood-face :mood="mood" :size="108"/>
    </div>
    <div v-else class="body-q">
      {{ text }}
    </div>

    <div class="foot">
      <span>{{ stamp }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.jcard {
  width: 100%;
  height: 100%;
  border-radius: 23px;
  padding: 32px;
  box-shadow: 0 6px 36px rgb(0 0 0 / 10%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  user-select: none;
  // The card keeps its mood colour in both themes (a sticky note on a dark
  // panel), so its ink is pinned dark rather than following the theme.
  color: var(--today-ink-on-pastel);
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pill {
  border: 2.5px solid var(--today-ink-on-pastel);
  border-radius: 142px;
  padding: 8px 24px;
  font-family: var(--Font-Primary-Font);
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 1.5px;
  color: var(--today-ink-on-pastel);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.body-q {
  font-family: var(--Font-Secondary-Font);
  font-weight: 300;
  font-size: 30px;
  line-height: 30px;
  letter-spacing: -0.75px;
  color: var(--today-ink-on-pastel);
  // Long entries ellipsise instead of overflowing the card.
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;
}

.big-face {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: var(--Font-Primary-Font);
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: var(--today-ink-on-pastel);
}
</style>
